import type { Corners, Point } from './geometry';

/** The working size: detection needs the document's outline, not its detail. */
const WORK_LONG_EDGE = 256;
/** A document is assumed to cover between 10% and 95% of the photo. */
const MIN_AREA_FRACTION = 0.1;
const MAX_AREA_FRACTION = 0.95;

/**
 * Proposes the four corners of a document photographed on a darker background, or null when no
 * plausible document is found (the caller then keeps its default corners, and the user corrects
 * them as before). On a small blurred grey copy: Otsu's threshold separates paper from background,
 * the largest light region is taken, and its extreme points along the two diagonals are the
 * corners. No dependency and a few milliseconds; the user's correction stays the fallback, as in
 * every major scanner app.
 */
export function detectDocumentCorners(image: ImageData): Corners | null {
  const scale = Math.min(1, WORK_LONG_EDGE / Math.max(image.width, image.height));
  const w = Math.max(1, Math.round(image.width * scale));
  const h = Math.max(1, Math.round(image.height * scale));
  const gray = blur(toSmallGray(image, w, h), w, h);
  const threshold = otsu(gray);

  const region = largestRegion(gray, w, h, threshold);
  if (region === null) return null;
  const area = w * h;
  if (region.size < MIN_AREA_FRACTION * area || region.size > MAX_AREA_FRACTION * area) return null;

  const back = (p: Point): Point => ({
    x: Math.min(image.width, Math.max(0, (p.x + 0.5) / scale)),
    y: Math.min(image.height, Math.max(0, (p.y + 0.5) / scale)),
  });
  const corners: Corners = [back(region.tl), back(region.tr), back(region.br), back(region.bl)];
  const quadArea = polygonArea(corners);
  if (!isConvex(corners) || quadArea < MIN_AREA_FRACTION * image.width * image.height) return null;
  return corners;
}

function toSmallGray(image: ImageData, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h);
  const sx = image.width / w;
  const sy = image.height / h;
  const d = image.data;
  for (let y = 0; y < h; y++) {
    const srcY = Math.min(image.height - 1, Math.floor((y + 0.5) * sy));
    for (let x = 0; x < w; x++) {
      const srcX = Math.min(image.width - 1, Math.floor((x + 0.5) * sx));
      const i = (srcY * image.width + srcX) * 4;
      out[y * w + x] = 0.299 * (d[i] ?? 0) + 0.587 * (d[i + 1] ?? 0) + 0.114 * (d[i + 2] ?? 0);
    }
  }
  return out;
}

/** The mean of the 5×5 window around (x, y), clipped at the image's edges. */
function windowMean(a: Float32Array, w: number, h: number, x: number, y: number): number {
  let sum = 0;
  let n = 0;
  for (let dy = -2; dy <= 2; dy++) {
    const yy = y + dy;
    if (yy < 0 || yy >= h) continue;
    for (let dx = -2; dx <= 2; dx++) {
      const xx = x + dx;
      if (xx < 0 || xx >= w) continue;
      sum += a[yy * w + xx] ?? 0;
      n++;
    }
  }
  return sum / n;
}

/** Two passes of a 5×5 box blur: removes text and texture inside the page. */
function blur(a: Float32Array, w: number, h: number): Float32Array {
  let cur = a;
  for (let pass = 0; pass < 2; pass++) {
    const next = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) next[y * w + x] = windowMean(cur, w, h, x, y);
    }
    cur = next;
  }
  return cur;
}

/** Otsu's threshold over a 256-bin histogram. */
function otsu(gray: Float32Array): number {
  const hist = new Array<number>(256).fill(0);
  for (const v of gray) hist[Math.max(0, Math.min(255, Math.round(v)))]! += 1;
  const total = gray.length;
  let sumAll = 0;
  for (let i = 0; i < 256; i++) sumAll += i * (hist[i] ?? 0);
  let sumBack = 0;
  let weightBack = 0;
  let best = 0;
  let bestVariance = -1;
  for (let t = 0; t < 256; t++) {
    weightBack += hist[t] ?? 0;
    if (weightBack === 0) continue;
    const weightFore = total - weightBack;
    if (weightFore === 0) break;
    sumBack += t * (hist[t] ?? 0);
    const meanBack = sumBack / weightBack;
    const meanFore = (sumAll - sumBack) / weightFore;
    const variance = weightBack * weightFore * (meanBack - meanFore) ** 2;
    if (variance > bestVariance) {
      bestVariance = variance;
      best = t;
    }
  }
  return best;
}

interface Region {
  size: number;
  tl: Point;
  tr: Point;
  br: Point;
  bl: Point;
}

interface Extremes {
  minSum: number;
  maxSum: number;
  minDiff: number;
  maxDiff: number;
}

/** Moves the region's diagonal extreme points to (x, y) where it goes beyond them. */
function trackExtremes(r: Region, e: Extremes, x: number, y: number): void {
  const sum = x + y;
  const diff = x - y;
  if (sum < e.minSum) {
    e.minSum = sum;
    r.tl = { x, y };
  }
  if (sum > e.maxSum) {
    e.maxSum = sum;
    r.br = { x, y };
  }
  if (diff > e.maxDiff) {
    e.maxDiff = diff;
    r.tr = { x, y };
  }
  if (diff < e.minDiff) {
    e.minDiff = diff;
    r.bl = { x, y };
  }
}

/** The largest 4-connected region lighter than `threshold`, with its diagonal extreme points. */
function largestRegion(gray: Float32Array, w: number, h: number, threshold: number): Region | null {
  const seen = new Uint8Array(w * h);
  const stack: number[] = [];
  const visit = (n: number): void => {
    if (!seen[n] && (gray[n] ?? 0) > threshold) {
      seen[n] = 1;
      stack.push(n);
    }
  };
  let best: Region | null = null;
  for (let start = 0; start < w * h; start++) {
    if (seen[start] || (gray[start] ?? 0) <= threshold) continue;
    seen[start] = 1;
    stack.push(start);
    const r: Region = {
      size: 0,
      tl: { x: w, y: h },
      tr: { x: 0, y: h },
      br: { x: 0, y: 0 },
      bl: { x: w, y: 0 },
    };
    const extremes = { minSum: Infinity, maxSum: -Infinity, minDiff: Infinity, maxDiff: -Infinity };
    while (stack.length > 0) {
      const i = stack.pop()!;
      const x = i % w;
      const y = (i - x) / w;
      r.size++;
      trackExtremes(r, extremes, x, y);
      if (x > 0) visit(i - 1);
      if (x < w - 1) visit(i + 1);
      if (y > 0) visit(i - w);
      if (y < h - 1) visit(i + w);
    }
    if (best === null || r.size > best.size) best = r;
  }
  return best;
}

function polygonArea(c: Corners): number {
  let a = 0;
  for (let i = 0; i < 4; i++) {
    const p = c[i]!;
    const q = c[(i + 1) % 4]!;
    a += p.x * q.y - q.x * p.y;
  }
  return Math.abs(a) / 2;
}

function isConvex(c: Corners): boolean {
  let sign = 0;
  for (let i = 0; i < 4; i++) {
    const a = c[i]!;
    const b = c[(i + 1) % 4]!;
    const d = c[(i + 2) % 4]!;
    const cross = (b.x - a.x) * (d.y - b.y) - (b.y - a.y) * (d.x - b.x);
    if (cross === 0) return false;
    const s = Math.sign(cross);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}
